import React, { useState, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { submitIntakeForm } from '../../services/api';
import { Loader2, AlertCircle, Send, ChevronDown, CheckCircle2, Lock, Sparkles, Eye, EyeOff, User, Smartphone, Save } from 'lucide-react';
import CelebrationScreen from '../../components/portal/CelebrationScreen';
import useFormDraft from '../../hooks/useFormDraft';

const toolCategories = [
    { key: 'crm_tools', label: 'CRM', placeholder: 'e.g. HubSpot, GoHighLevel, Salesforce' },
    { key: 'pm_tools', label: 'Project Management', placeholder: 'e.g. Asana, ClickUp, Monday' },
    { key: 'phone_tools', label: 'Phone System', placeholder: 'e.g. RingCentral, Aircall, Dialpad' },
    { key: 'meeting_tools', label: 'Meetings / Recording', placeholder: 'e.g. Granola, Fathom, Zoom' },
    { key: 'comms_tools', label: 'Team Comms', placeholder: 'e.g. Slack, Teams, Discord' },
    { key: 'scheduling_tools', label: 'Scheduling', placeholder: 'e.g. Calendly, Acuity, HousecallPro' },
    { key: 'accounting_tools', label: 'Accounting', placeholder: 'e.g. QuickBooks, Xero, FreshBooks' },
    { key: 'field_tools', label: 'Field Service', placeholder: 'e.g. JobNimbus, AccuLynx, CompanyCam' },
    { key: 'marketing_tools', label: 'Marketing', placeholder: 'e.g. ActiveCampaign, Mailchimp, Constant Contact' },
    { key: 'other_tools', label: 'Other', placeholder: 'Anything else your business runs on' },
];

const inputCls = "w-full bg-[#18181B] border border-[#F0EFF4]/10 rounded-xl px-4 py-3 text-[#F0EFF4] placeholder:text-[#F0EFF4]/25 focus:border-[#7B61FF] focus:outline-none transition-colors";
const selectCls = `${inputCls} appearance-none`;
const textareaCls = `${inputCls} resize-none`;

const Field = ({ label, children, span2 }) => (
    <div className={span2 ? 'md:col-span-2' : ''}>
        <label className="block text-sm font-semibold text-[#F0EFF4]/70 mb-2">{label}</label>
        {children}
    </div>
);

const Divider = ({ title }) => (
    <div className="flex items-center gap-4 pt-10 pb-2">
        <div className="h-px flex-1 bg-[#F0EFF4]/5" />
        <span className="text-xs font-data font-semibold text-[#7B61FF] uppercase tracking-[0.2em]">{title}</span>
        <div className="h-px flex-1 bg-[#F0EFF4]/5" />
    </div>
);

const RadioRow = ({ name, options }) => (
    <div className="flex flex-wrap gap-2 w-full">
        {options.map(opt => (
            <label key={opt} className="flex-1 min-w-[120px] flex items-center justify-center gap-2 px-3 py-2.5 border border-[#F0EFF4]/10 rounded-xl bg-[#18181B] cursor-pointer hover:border-[#7B61FF]/40 has-[:checked]:border-[#7B61FF] has-[:checked]:bg-[#7B61FF]/10 transition-colors">
                <input type="radio" value={opt} name={name} className="hidden" />
                <span className="text-sm font-medium text-[#F0EFF4]/80 text-center">{opt}</span>
            </label>
        ))}
    </div>
);

const SkillCard = ({ name, label, emoji, description, cost, helpUrl, helpText }) => {
    const [revealed, setRevealed] = React.useState(false);
    return (
        <div className="bg-[#18181B] border border-[#F0EFF4]/10 rounded-xl p-4">
            <div className="flex items-start gap-3">
                <span className="text-xl mt-0.5">{emoji}</span>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-[#F0EFF4] text-sm">{label}</span>
                        <span className="text-xs text-[#F0EFF4]/30 font-data">{cost}</span>
                    </div>
                    <p className="text-xs text-[#F0EFF4]/50 mb-3">{description}</p>
                    <div className="relative">
                        <input
                            type={revealed ? 'text' : 'password'}
                            name={`skill_key_${name}`}
                            placeholder="Paste API key here (optional)"
                            className={`${inputCls} pr-10 text-sm`}
                        />
                        <button
                            type="button"
                            onClick={() => setRevealed(r => !r)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F0EFF4]/30 hover:text-[#F0EFF4]/60 transition-colors"
                        >
                            {revealed ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                    </div>
                    {helpUrl && (
                        <p className="text-xs text-[#F0EFF4]/20 mt-1.5">
                            Get your key: <a href={helpUrl} target="_blank" rel="noopener noreferrer" className="text-[#7B61FF]/60 hover:text-[#7B61FF] transition-colors">{helpText || helpUrl}</a>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
};

const IntakeForm = () => {
    const { token, clientData } = useOutletContext();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const formRef = useRef(null);

    const isComplete = clientData?.progress?.intake_complete || clientData?.completed_steps?.includes('intake');
    const { draftRestored, draftSavedAt, clearDraft } = useFormDraft(token, formRef, { disabled: isComplete });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isComplete) return; // Guard: never re-submit a locked form
        setSubmitting(true);
        setError('');

        const fd = new FormData(e.target);
        const data = Object.fromEntries(fd.entries());

        try {
            await submitIntakeForm(token, clientData, data);
            clearDraft();
            setShowSuccess(true);
        } catch (err) {
            console.error("Submission failed:", err);
            setError("There was a problem submitting. Please try again.");
            setSubmitting(false);
        }
    };

    // --- Reusable primitives moved outside ---

    if (showSuccess) {
        const firstName = (clientData?.name || clientData?.client_name || 'Client').split(' ')[0];
        return (
            <CelebrationScreen
                emoji="🚀"
                title={`You're officially onboarding, ${firstName}!`}
                subtitle="Your intake has been received and your project is moving forward. We'll review everything and be in touch shortly to schedule your build kickoff."
                nextLabel="Return to Portal"
                onNext={() => navigate(`/onboard/${token}`)}
                intensity="high"
            />
        );
    }

    return (
        <div className="animate-fade-in max-w-2xl mx-auto pb-24">
            {/* Header */}
            <header className="mb-10">
                <h1 className="text-2xl font-bold mb-1">Client Intake</h1>
                <p className="text-[#F0EFF4]/40 text-sm">Complete this form to provision your hardware and agent framework.</p>
            </header>

            {isComplete && (
                <div className="bg-[#18181B] border border-[#F0EFF4]/10 p-5 rounded-2xl mb-8 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#10B981]/10 flex items-center justify-center shrink-0 mt-0.5">
                        <Lock className="w-5 h-5 text-[#10B981]" />
                    </div>
                    <div>
                        <h3 className="font-bold text-[#F0EFF4] mb-1">Already Submitted</h3>
                        <p className="text-[#F0EFF4]/60 text-sm">
                            This intake form is locked for processing. Contact us via your preferred VIP channel if you need to make changes.
                        </p>
                    </div>
                </div>
            )}

            {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-xl mb-6 flex items-center gap-2 text-sm font-medium">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                </div>
            )}

            {/* Draft status indicators */}
            {draftRestored && (
                <div className="bg-[#7B61FF]/10 border border-[#7B61FF]/30 text-[#7B61FF] p-3 rounded-xl mb-6 flex items-center gap-2 text-sm font-medium animate-fade-in">
                    <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                    Draft restored — your previous progress has been loaded.
                </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} className="space-y-1">
                <fieldset disabled={isComplete} className="space-y-1 disabled:opacity-80">

                    {/* ---- COMPANY & OWNER ---- */}
                    <Divider title="Company & Owner" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5 pt-4">
                        <Field label="Owner Name">
                            <input required type="text" name="owner_name" defaultValue={clientData?.name || ''} className={inputCls} />
                        </Field>
                        <Field label="Role / Title">
                            <input type="text" name="owner_role" className={inputCls} placeholder="e.g. Owner, CEO" />
                        </Field>
                        <Field label="Company Name">
                            <input required type="text" name="company_name" defaultValue={clientData?.company || ''} className={inputCls} />
                        </Field>
                        <Field label="Website">
                            <input type="url" name="company_website" placeholder="https://" className={inputCls} />
                        </Field>
                        <Field label="Team Size">
                            <div className="relative">
                                <select name="team_size" className={selectCls}>
                                    <option value="">Select</option>
                                    <option>1-5</option>
                                    <option>6-15</option>
                                    <option>16-50</option>
                                    <option>50+</option>
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F0EFF4]/30 pointer-events-none" />
                            </div>
                        </Field>
                        <Field label="Location">
                            <input type="text" name="location" placeholder="City, State" className={inputCls} />
                        </Field>
                        <Field label="Primary Specialization" span2>
                            <input type="text" name="specialization" placeholder="e.g. Residential Asphalt, Commercial TPO" className={inputCls} />
                        </Field>
                    </div>

                    {/* ---- PRE-REQUISITE EMAILS ---- */}
                    <Divider title="Pre-Requisite Accounts" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5 pt-4">
                        <Field label="Agent Email Address">
                            <input type="email" name="agent_email" placeholder="agent@yourdomain.com" className={inputCls} />
                        </Field>
                        <Field label="Tailscale Email">
                            <input type="email" name="tailscale_email" placeholder="tailscale@yourdomain.com" className={inputCls} />
                        </Field>
                    </div>

                    {/* ---- TECH STACK ---- */}
                    <Divider title="Tech Stack" />
                    <p className="text-xs text-[#F0EFF4]/30 pt-2 pb-3">Type the specific tool name for each category your business uses. Leave blank if N/A.</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
                        {toolCategories.map(tool => (
                            <Field key={tool.key} label={tool.label}>
                                <input type="text" name={tool.key} placeholder={tool.placeholder} className={inputCls} />
                            </Field>
                        ))}
                    </div>

                    {/* ---- COMMUNICATION ---- */}
                    <Divider title="Communication" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5 pt-4">
                        <Field label="Preferred VIP Channel">
                            <div className="relative">
                                <select name="preferred_channel" className={selectCls}>
                                    <option value="">Select</option>
                                    <option>Telegram</option>
                                    <option>WhatsApp</option>
                                    <option>iMessage</option>
                                    <option>Slack</option>
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F0EFF4]/30 pointer-events-none" />
                            </div>
                        </Field>
                        <Field label="Timezone & Active Hours">
                            <input type="text" name="active_hours" placeholder="e.g. EST, 8am–6pm" className={inputCls} />
                        </Field>
                        <Field label="Proactive Check-ins?" span2>
                            <RadioRow name="proactive_checkins" options={['Yes', 'Sometimes', 'No, I will ping it']} />
                        </Field>
                    </div>

                    {/* ---- AGENT PERSONA ---- */}
                    <Divider title="Agent Persona" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5 pt-4">
                        <Field label="Agent Name">
                            <input type="text" name="agent_name" placeholder="e.g. Alfred, Oracle" className={inputCls} />
                        </Field>
                        <Field label="Agent Role">
                            <input type="text" name="agent_role" placeholder="e.g. Chief of Staff" className={inputCls} />
                        </Field>
                        <Field label="Communication Style" span2>
                            <RadioRow name="comm_style" options={['Clinical & Direct', 'Warm & Conversational', 'High-Energy & Motivating']} />
                        </Field>
                        <Field label="Push-back Level" span2>
                            <RadioRow name="pushback_level" options={['Low — Executes literally', 'Medium — Questions mistakes', 'High — Challenges strategy']} />
                        </Field>
                        <Field label="Values & Guardrails" span2>
                            <textarea name="values_guardrails" rows="3" placeholder="Core business values, rules the agent must always follow, things it should never do..." className={textareaCls} />
                        </Field>
                    </div>

                    {/* ---- EXECUTION MANDATE ---- */}
                    <Divider title="Execution Mandate" />
                    <div className="space-y-5 pt-4">
                        <Field label="What is the #1 problem you want this agent to solve right now?" span2>
                            <textarea required name="top_problem" rows="3" className={textareaCls} />
                        </Field>

                        <Field label="3 specific tasks to hand off this month" span2>
                            <div className="space-y-3">
                                <input required type="text" name="priority_1" placeholder="Task 1" className={inputCls} />
                                <input type="text" name="priority_2" placeholder="Task 2" className={inputCls} />
                                <input type="text" name="priority_3" placeholder="Task 3" className={inputCls} />
                            </div>
                        </Field>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5">
                            <Field label="30-Day Success Plan">
                                <textarea required name="success_30_days" rows="4" placeholder="What does success look like at Day 30?" className={textareaCls} />
                            </Field>
                            <Field label="90-Day Success Plan">
                                <textarea required name="success_90_days" rows="4" placeholder="What does success look like at Day 90?" className={textareaCls} />
                            </Field>
                        </div>

                        <Field label="What are you currently paying someone to do that the agent could handle?" span2>
                            <textarea name="could_replace" rows="2" className={textareaCls} />
                        </Field>
                        <Field label="What should the agent brief you on every morning?" span2>
                            <input type="text" name="morning_briefing" placeholder="e.g. Yesterday's sales numbers, unresolved tickets" className={inputCls} />
                        </Field>
                        <Field label="Additional Notes" span2>
                            <textarea name="additional_notes" rows="3" placeholder="Anything else we should know..." className={textareaCls} />
                        </Field>
                    </div>

                    {/* ---- TECHNICAL CONTACT ---- */}
                    <Divider title="Technical Contact (Optional)" />
                    <p className="text-xs text-[#F0EFF4]/30 pt-2 pb-3">
                        If someone handles your IT or network setup, we may need to coordinate with them for Tailscale and remote access.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5 pt-2">
                        <Field label="Tech Contact Name">
                            <input type="text" name="tech_contact_name" placeholder="e.g. Giovanni Hernández" className={inputCls} />
                        </Field>
                        <Field label="Tech Contact Email">
                            <input type="email" name="tech_contact_email" placeholder="giovanni@example.com" className={inputCls} />
                        </Field>
                    </div>

                    {/* ---- CRM API ACCESS ---- */}
                    <div className="grid grid-cols-1 gap-y-5 pt-6">
                        <Field label="CRM API Token (Optional)" span2>
                            <input type="password" name="crm_api_token" placeholder="If your CRM has API access, paste the token here" className={inputCls} />
                            <p className="text-xs text-[#F0EFF4]/20 mt-1.5">This lets your agent connect directly to your CRM. Transmitted securely over HTTPS.</p>
                        </Field>
                    </div>

                    {/* ---- CHANNEL PREFERENCE ---- */}
                    <div className="grid grid-cols-1 gap-y-5 pt-6">
                        <Field label="Agent Channel Preference" span2>
                            <RadioRow name="channel_preference" options={['Telegram (default)', 'WhatsApp (requires additional setup)', 'Both']} />
                        </Field>
                    </div>

                    {/* ---- POWER UP YOUR AGENT ---- */}
                    <Divider title="Power Up Your Agent (Optional)" />
                    <p className="text-xs text-[#F0EFF4]/30 pt-2 pb-3">
                        These integrations give your agent extra abilities. You can add them now or anytime later. All keys are transmitted securely and stored encrypted.
                    </p>
                    <div className="space-y-4 pt-2">
                        <SkillCard
                            name="elevenlabs"
                            label="ElevenLabs"
                            emoji="🗣️"
                            description="Gives your agent a real voice (text-to-speech)"
                            cost="~$5–$22/mo depending on usage"
                            helpUrl="https://elevenlabs.io"
                            helpText="Profile → API Key"
                        />
                        <SkillCard
                            name="openai"
                            label="OpenAI"
                            emoji="🎨"
                            description="Speech-to-text transcription + AI image generation (one key covers both)"
                            cost="~$0.006/min transcription, ~$0.04/image"
                            helpUrl="https://platform.openai.com/api-keys"
                            helpText="API Keys → Create new"
                        />
                        <SkillCard
                            name="google_places"
                            label="Google Places"
                            emoji="📍"
                            description="Look up business locations, hours, and reviews"
                            cost="$200 free credit/mo, then ~$0.017/request"
                            helpUrl="https://console.cloud.google.com"
                            helpText="APIs & Services → Credentials"
                        />
                        <SkillCard
                            name="gemini"
                            label="Gemini"
                            emoji="✨"
                            description="AI image generation via Google"
                            cost="Free tier available"
                            helpUrl="https://aistudio.google.com/apikey"
                            helpText="Get API Key"
                        />
                        <SkillCard
                            name="notion"
                            label="Notion"
                            emoji="📝"
                            description="Connects your agent to your Notion workspace"
                            cost="Free API key"
                            helpUrl="https://www.notion.so/my-integrations"
                            helpText="My Integrations → New"
                        />
                    </div>

                    {/* ---- SUBMIT ---- */}
                    {!isComplete && (
                        <div className="pt-10">
                            {draftSavedAt && (
                                <p className="text-xs text-[#F0EFF4]/30 mb-3 flex items-center gap-1.5 justify-center">
                                    <Save className="w-3 h-3" />
                                    Draft saved — your progress is safe if you leave this page.
                                </p>
                            )}
                            <button
                                disabled={submitting}
                                type="submit"
                                className="w-full py-4 bg-[#7B61FF] hover:bg-[#6b52e8] text-white font-bold rounded-xl flex items-center justify-center gap-3 transition-all disabled:opacity-40 shadow-[0_0_24px_rgba(123,97,255,0.25)]"
                            >
                                {submitting ? (
                                    <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</>
                                ) : (
                                    <><Send className="w-4 h-4" /> Submit Intake</>
                                )}
                            </button>
                        </div>
                    )}
                </fieldset>
            </form>
        </div>
    );
};

export default IntakeForm;
